import Swiper from 'swiper';
import { A11y, Autoplay, EffectFade } from 'swiper/modules';
import type { SwiperOptions } from 'swiper/types';

/**
 * Hero ブロック — メインビジュアルの3スライダー管理
 *
 * - js-hero-main-swiper: ページ読み込み直後に自動再生
 * - js-hero-sub1-swiper / js-hero-sub2-swiper:
 *     ScrollAnimation が is-visible を付与したタイミングで自動再生を開始
 *     （スクロールアニメーション完了まで待機するため MutationObserver を使用）
 */
export class Hero {
  private readonly el: HTMLElement;
  private swipers: Swiper[] = [];
  private observers: MutationObserver[] = [];
  private kenBurnsCount = 0;

  constructor(el: HTMLElement) {
    this.el = el;
  }

  init(): void {
    this.initMainSwiper();
    this.initSubSwiper('.js-hero-sub1-swiper', 3100);
    this.initSubSwiper('.js-hero-sub2-swiper', 4500);
  }

  private static randInt(min: number, max: number): number {
    return Math.floor(Math.random() * (max - min + 1)) + min;
  }

  /**
   * @param delay autoplay の待機時間（ms）
   *   main: 4200 / sub1: 3100 / sub2: 4500
   *   互いに公倍数を持ちにくい値にして同期を防ぐ
   */
  private fadeOptions(delay: number): SwiperOptions {
    return {
      modules: [Autoplay, EffectFade, A11y],
      effect: 'fade',
      fadeEffect: { crossFade: true },
      speed: 1500,
      loop: true,
      initialSlide: Hero.randInt(0, 2),
      autoplay: {
        delay,
        disableOnInteraction: false,
      },
      allowTouchMove: false,
      a11y: { enabled: true },
    };
  }

  /**
   * Ken Burns アニメーションをリスタート
   * zoom-in / zoom-out を交互に切り替え、スライドが替わるたびに呼び出す
   */
  private startKenBurns(swiper: Swiper): void {
    const slide = swiper.slides[swiper.activeIndex] as HTMLElement | undefined;
    const img = slide?.querySelector<HTMLElement>('.p-hero__main-img');
    if (!img) return;
    const name = this.kenBurnsCount % 2 === 0 ? 'p-hero-kenburns-in' : 'p-hero-kenburns-out';
    img.style.animationName = 'none';
    void img.offsetWidth; // reflow で animation をリセット
    img.style.animationName = name;
    this.kenBurnsCount++;
  }

  /** メインスライダー: ロード後すぐ自動再生 + Ken Burns */
  private initMainSwiper(): void {
    const el = this.el.querySelector<HTMLElement>('.js-hero-main-swiper');
    if (!el) return;
    const swiper = new Swiper(el, {
      ...this.fadeOptions(4200),
      on: {
        afterInit: (s) => this.startKenBurns(s),
        slideChangeTransitionStart: (s) => this.startKenBurns(s),
      },
    });
    this.swipers.push(swiper);
  }

  /**
   * サブスライダー: Swiper を初期化した直後に autoplay を止め、
   * js-scroll-animation が is-visible を付与したら再開する。
   */
  private initSubSwiper(selector: string, delay: number): void {
    const el = this.el.querySelector<HTMLElement>(selector);
    if (!el) return;

    const swiper = new Swiper(el, this.fadeOptions(delay));
    swiper.autoplay.stop();
    this.swipers.push(swiper);

    const obs = new MutationObserver(() => {
      if (el.classList.contains('is-visible')) {
        swiper.autoplay.start();
        obs.disconnect();
      }
    });
    obs.observe(el, { attributes: true, attributeFilter: ['class'] });
    this.observers.push(obs);
  }

  destroy(): void {
    this.observers.forEach((obs) => obs.disconnect());
    this.observers = [];
    this.swipers.forEach((s) => s.destroy(true, true));
    this.swipers = [];
  }
}
